/**
 * kbWriteOps — the KB WRITE facade (upload / edit / download / delete). Both read
 * (GetKbTree) and write are now owner-generic: one op per action, keyed by
 * {ownerType, ownerId}. So there's NO per-owner dispatch left here — each method is a
 * thin pass-through that forwards ownerType/ownerId to the single generic command. Kept
 * as a facade (not inlined into useKb) so the op names / URIs live in one place and
 * useKb + the panel stay decoupled from the command layer.
 *
 * ownerType is passed straight through (ASSET / UC / STORYLINE — whatever the backend
 * supports; a not-yet-supported owner surfaces the backend's error, no frontend gate).
 * ownerId is the string the UI carries (assetId, or the stringified solutionMapId).
 */
import {
  getIvtClient,
  // Owner-generic file write ops (one op per action, keyed by {ownerType, ownerId}).
  RequestKbFileUploadUrlCommand,
  ConfirmKbFileUploadCommand,
  RequestKbFileEditUrlCommand,
  ConfirmKbFileEditGenericCommand,
  GetKbFileDownloadUrlGenericCommand,
  DeleteKbEntryCommand,
  // URL entries (a link, not a file). Add creates; Update is a partial edit. Delete/move
  // reuse DeleteKbEntry / MoveKbEntry.
  AddKbUrlEntryCommand,
  UpdateKbUrlEntryCommand,
  // Overview first-save (owner-generic; subsequent edits reuse the file edit ops above).
  RequestKbOverviewUploadUrlCommand,
  ConfirmKbOverviewUploadCommand,
  KbDownloadDisposition,
  type KbContentType,
  type KbOwnerType,
} from './IvtHubClient';

/** The owner an op targets. ownerId is the string the UI carries (assetId, or the
 *  stringified solutionMapId for UC). */
export interface KbOwner {
  ownerType: KbOwnerType;
  ownerId: string;
}

export const kbWriteOps = {
  /** Mint a presigned upload URL. → { fileId, uploadUrl }. folderId optional: absent =
   *  root; present = drop the file into that folder (must belong to this owner). */
  async requestUploadUrl(
    owner: KbOwner,
    filename: string,
    contentType: KbContentType,
    folderId?: string,
  ): Promise<{ fileId?: string; uploadUrl?: string }> {
    return getIvtClient().send(
      new RequestKbFileUploadUrlCommand({
        ownerType: owner.ownerType,
        ownerId: owner.ownerId,
        filename,
        contentType,
        folderId,
      }),
    );
  },

  /** Confirm an uploaded file (flips the row to `uploaded`). */
  async confirmUpload(owner: KbOwner, fileId: string): Promise<void> {
    await getIvtClient().send(
      new ConfirmKbFileUploadCommand({ ownerType: owner.ownerType, ownerId: owner.ownerId, fileId }),
    );
  },

  /** Delete one entry by fileId (entry-level — file or url). 204 on success. */
  async remove(owner: KbOwner, fileId: string): Promise<void> {
    await getIvtClient().send(
      new DeleteKbEntryCommand({ ownerType: owner.ownerType, ownerId: owner.ownerId, fileId }),
    );
  },

  /** Mint a presigned GET URL (attachment = download, inline = preview). → downloadUrl. */
  async downloadUrl(
    owner: KbOwner,
    fileId: string,
    disposition: KbDownloadDisposition,
  ): Promise<string | undefined> {
    const out = await getIvtClient().send(
      new GetKbFileDownloadUrlGenericCommand({
        ownerType: owner.ownerType,
        ownerId: owner.ownerId,
        fileId,
        disposition,
      }),
    );
    return out.downloadUrl;
  },

  /** Mint a presigned edit PUT URL (same fileId/S3 key). → editUrl. */
  async requestEditUrl(owner: KbOwner, fileId: string): Promise<string | undefined> {
    const out = await getIvtClient().send(
      new RequestKbFileEditUrlCommand({ ownerType: owner.ownerType, ownerId: owner.ownerId, fileId }),
    );
    return out.editUrl;
  },

  /** Overview FIRST save, step 1: mint a presigned PUT URL for the owner's Overview
   *  doc. No fileId in — the server computes the deterministic Overview id and returns
   *  it. These two Overview ops are already owner-generic (URI carries ownerType), so
   *  no per-owner dispatch. Subsequent edits reuse requestEditUrl / confirmEdit. */
  async requestOverviewUploadUrl(owner: KbOwner): Promise<{ fileId?: string; uploadUrl?: string }> {
    return getIvtClient().send(
      new RequestKbOverviewUploadUrlCommand({ ownerType: owner.ownerType, ownerId: owner.ownerId }),
    );
  },

  /** Overview FIRST save, step 2: confirm the bytes landed. Returns the confirmed
   *  Overview entry. */
  async confirmOverviewUpload(owner: KbOwner, fileId: string): Promise<void> {
    await getIvtClient().send(
      new ConfirmKbOverviewUploadCommand({ ownerType: owner.ownerType, ownerId: owner.ownerId, fileId }),
    );
  },

  /** Confirm an in-place edit (recomputes size/updatedAt). */
  async confirmEdit(owner: KbOwner, fileId: string): Promise<void> {
    await getIvtClient().send(
      new ConfirmKbFileEditGenericCommand({ ownerType: owner.ownerType, ownerId: owner.ownerId, fileId }),
    );
  },

  /** Add a URL entry (a link, not a file — not ingested). folderId optional (root when
   *  absent). Server generates the fileId + returns the created KbEntry. */
  async addUrl(
    owner: KbOwner,
    displayName: string,
    url: string,
    folderId?: string,
  ): Promise<void> {
    await getIvtClient().send(
      new AddKbUrlEntryCommand({ ownerType: owner.ownerType, ownerId: owner.ownerId, displayName, url, folderId }),
    );
  },

  /** Edit a URL entry (partial: only the fields passed change; both omitted = no-op). */
  async updateUrl(
    owner: KbOwner,
    fileId: string,
    fields: { displayName?: string; url?: string },
  ): Promise<void> {
    await getIvtClient().send(
      new UpdateKbUrlEntryCommand({
        ownerType: owner.ownerType,
        ownerId: owner.ownerId,
        fileId,
        displayName: fields.displayName,
        url: fields.url,
      }),
    );
  },
};

/** Re-export so callers reference dispositions without a second import. */
export { KbDownloadDisposition };
